import { useCallback } from 'react';
import { locationsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Location } from '../types';

export function useLocations(
  options?: ResourceQueryOptions
): ResourceState<Location> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      locationsService.getAll(opts) as unknown as Promise<{ data: Location[]; meta?: any }>,
    []
  );
  return useResourceCollection<Location>(fetchFn, options);
}

export function useLocation(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Location> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (locationId: number, opts?: ResourceQueryOptions) =>
      locationsService.getById(locationId, opts) as unknown as Promise<Location>,
    []
  );
  return useResourceById<Location>(fetchFn, numericId, options);
}

export function useCreateLocation(
  onSuccess?: (data: Location) => void
): MutationState<Location> {
  const createFn = useCallback(
    (data: any) => locationsService.create(data) as unknown as Promise<Location>,
    []
  );
  return useCreateResource<Location>(createFn, onSuccess);
}

export function useUpdateLocation(
  onSuccess?: (data: Location) => void
): MutationState<Location> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return locationsService.update(numericId, data) as unknown as Promise<Location>;
    },
    []
  );
  return useUpdateResource<Location>(updateFn, onSuccess);
}

/**
 * Delete location using documentId
 * @param data - Full location object containing documentId
 */
export function useDeleteLocation(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return locationsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

